import { useFindMany } from "@gadgetinc/react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/16/solid";
import { PlusIcon } from "@heroicons/react/20/solid";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import ArchiveModal from "../../externalComponents/ArchiveModal";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import { Badge } from "../../externalComponents/tailwind/badge";
import { Button } from "../../externalComponents/tailwind/button";
import EmptyState from "../../externalComponents/tailwind/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../externalComponents/tailwind/table";
import { CustomerContext } from "../../providers";
import ChatbotsActionsDropdown from "./shared/ChatbotsActionsDropdown";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../externalComponents/tailwind/dialog";
import { useIsMobile } from "../../externalComponents/useIsMobile";

const ChatbotsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const { user, customer } = useContext(CustomerContext);
  const NUM_ON_PAGE = 10;
  const _defaultPartialFields = {
    id: true,
    updatedAt: true,
    createdAt: true,
    name: true,
    type: true,
    chatbotType: true,
    status: true,
    triggerCount: true,
    rootNode: {
      id: true,
      whatsappTrigger: {
        id: true,
      },
    },
  };

  const [search, setSearch] = useState("");
  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const [listData, setListData] = useState({
    isModalOpen: false,
    isModalArchiveOpen: false,
    isModalDuplicateOpen: false,
    chatbotToChange: null,
    isStatusLoading: false,
    loadingRow: null,
  });

  const updateIsModalOpen = useCallback(
    (isModalOpen) => {
      setListData((prevState) => ({
        ...prevState,
        isModalOpen,
      }));
    },
    [listData.isModalOpen],
  );

  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });
  const [pageNumber, setPageNumber] = useState(1);

  // URL parameter handling states
  const [targetPageFromUrl, setTargetPageFromUrl] = useState(null);
  const [isNavigatingFromUrl, setIsNavigatingFromUrl] = useState(false);
  const [isLoadingToTargetPage, setIsLoadingToTargetPage] = useState(false);

  const [criteria, setCriteria] = useState({
    ...cursor,
    filter: [
      { customerId: { equals: user.customerId } },
      { type: { equals: "chatbot" } },
      { isArchived: { notEquals: true } },
    ],
  });

  const [{ data: searchData }] = useFindMany(api.scenario, {
    search,
    select: { id: true },
  });

  useEffect(() => {
    if (search) {
      if (searchData) {
        const ids = searchData.map(({ id }) => id);
        setCriteria({
          filter: [
            { customerId: { equals: user.customerId } },
            { id: { in: ids.length > 0 ? ids : [0] } },
            { type: { equals: "chatbot" } },
            { isArchived: { notEquals: true } },
          ],
        });
      }
    } else {
      setCriteria({
        ...cursor,
        filter: [
          { customerId: { equals: user.customerId } },
          { type: { equals: "chatbot" } },
          { isArchived: { notEquals: true } },
        ],
      });
    }
  }, [searchData, search]);

  // Initialize page number from URL parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get("page");
    const initialPage = pageParam ? parseInt(pageParam, 10) : 1;

    if (initialPage > 1) {
      setTargetPageFromUrl(initialPage);
      setIsNavigatingFromUrl(true);
      setIsLoadingToTargetPage(true);
    } else {
      setTargetPageFromUrl(null);
      setIsNavigatingFromUrl(false);
      setIsLoadingToTargetPage(false);
    }
  }, [location.search]);

  const [{ data, fetching }] = useFindMany(api.scenario, {
    ...criteria,
    select: _defaultPartialFields,
    sort: {
      createdAt: "Descending",
    },
  });

  const handleDuplicateConfirmation = async () => {
    setIsDuplicateLoading(true);
    const newChatbot = await api.duplicateChatbotNodesAndEdges({
      oldScenarioId: listData?.chatbotToChange.id,
      customerId: user.customer.id,
    });
    navigate(`/chatbots/edit/${newChatbot.id}`);
  };

  const changePage = useCallback(
    (_cursor, _pageNumber) => {
      setCursor(_cursor);
      setPageNumber(_pageNumber);

      // Only update URL parameter if we're not navigating from URL
      if (!isNavigatingFromUrl) {
        const searchParams = new URLSearchParams(location.search);
        if (_pageNumber === 1) {
          searchParams.delete("page");
        } else {
          searchParams.set("page", _pageNumber.toString());
        }

        const newSearch = searchParams.toString();
        const newUrl = newSearch
          ? `${location.pathname}?${newSearch}`
          : location.pathname;
        navigate(newUrl, { replace: true });
      }

      setCriteria({
        ..._cursor,
        filter: [
          { customerId: { equals: user.customerId } },
          { type: { equals: "chatbot" } },
          { isArchived: { notEquals: true } },
        ],
      });
    },
    [
      location.search,
      location.pathname,
      navigate,
      user.customerId,
      isNavigatingFromUrl,
    ],
  );

  const getNextPage = () => {
    if (data?.endCursor) {
      changePage({ first: NUM_ON_PAGE, after: data.endCursor }, pageNumber + 1);
    }
  };

  // Handle navigation to correct page from URL parameter
  useEffect(() => {
    if (
      targetPageFromUrl &&
      targetPageFromUrl > 1 &&
      data &&
      data.length > 0 &&
      pageNumber < targetPageFromUrl &&
      !fetching
    ) {
      // We have data and need to navigate to the target page
      // Call getNextPage to move to the next page
      if (data?.endCursor) {
        getNextPage();
      }
    } else if (targetPageFromUrl && pageNumber === targetPageFromUrl) {
      // We've reached the target page, clear the target and navigation flag
      setTargetPageFromUrl(null);
      setIsNavigatingFromUrl(false);
      setIsLoadingToTargetPage(false);
    }
  }, [data, targetPageFromUrl, pageNumber, fetching]);

  const getPreviousPage = () => {
    if (pageNumber > 1) {
      changePage(
        { last: NUM_ON_PAGE, before: data.startCursor },
        pageNumber - 1,
      );
    }
  };

  const ChatbotList = () => (
    <div className="mx-3 md:mx-0">
      {fetching || isLoadingToTargetPage ? (
        <LoadingSpinner />
      ) : data && data.length > 0 ? (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader className="w-[25%]">Name</TableHeader>
              {!isMobile && <TableHeader>Creation date</TableHeader>}
              <TableHeader className="w-[15%] text-center">Status</TableHeader>
              <TableHeader className="text-center">Triggered</TableHeader>
              {!isMobile && (
                <TableHeader className="text-center">Actions</TableHeader>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow
                key={row.id}
                {...(!isMobile && { href: `/chatbots/edit/${row.id}` })}
              >
                <TableCell className="w-[40%] md:w-[20%]">{row.name}</TableCell>
                {!isMobile && (
                  <TableCell>
                    {row.createdAt.toLocaleDateString("en-US", {
                      day: "numeric",
                      month: "short",
                      hour: "numeric",
                      minute: "numeric",
                    })}
                  </TableCell>
                )}
                <TableCell className="text-center">
                  {listData.isStatusLoading &&
                  listData.loadingRow === row.id ? (
                    <div className="flex justify-center h-full p-0 m-0">
                      <div className="loader border-r-yellow w-[25px] p-0 m-0"></div>
                    </div>
                  ) : (
                    <Badge color={row.status === "active" ? "lime" : "rose"}>
                      {row.status}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {row.triggerCount}
                </TableCell>
                {!isMobile && (
                  <TableCell className="text-center">
                    <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                      <ChatbotsActionsDropdown
                        customerId={customer.id}
                        setListData={setListData}
                        chatbot={row}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <EmptyState
          heading="You have no chatbots"
          action={async () => {
            if (customer.isSubscribed) {
              navigate("/chatbots/new");
            }
          }}
          icon={<PlusIcon />}
          actionText="Create"
          img="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        />
      )}
      {!search && !isLoadingToTargetPage && (
        <div className="flex justify-start my-4">
          <Button
            color="secondaryButton"
            onClick={getPreviousPage}
            disabled={!data?.hasPreviousPage}
          >
            <ArrowLeftIcon />
            Previous
          </Button>
          <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
            {pageNumber}
          </Badge>
          <Button
            color="secondaryButton"
            onClick={getNextPage}
            disabled={!data?.hasNextPage}
          >
            Next
            <ArrowRightIcon />
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <PageHeading
        title="Chatbots"
        searchPlaceholder="Search in chatbots"
        search={search}
        setSearch={setSearch}
        {...(!isMobile && {
          primaryAction: "Create",
          handlePrimaryAction: async () => {
            navigate("/chatbots/new");
          },
        })}
        iconPrimaryAction={<PlusIcon />}
        isPrimaryDisabled={!customer.isSubscribed}
      />
      {isLoadingToTargetPage && <LoadingSpinner />}
      <OverflowFrame>
        <ChatbotList />
      </OverflowFrame>
      {listData.chatbotToChange && (
        <ArchiveModal
          customer={customer}
          scenario={listData.chatbotToChange}
          isModalArchiveOpen={listData.isModalOpen}
          setIsModalArchiveOpen={updateIsModalOpen}
          pageEdit={false}
        />
      )}
      {listData.chatbotToChange && (
        <Dialog
          key="dialog_duplicate"
          open={listData.isModalDuplicateOpen}
          onClose={() => {
            if (!isDuplicateLoading) {
              setListData((_listData) => ({
                ..._listData,
                isModalDuplicateOpen: false,
              }));
            }
          }}
        >
          <DialogTitle close={!isDuplicateLoading}>
            Duplicate this chatbot{" "}
          </DialogTitle>
          <DialogBody className="flex-1 overflow-y-auto scrollbar">
            <p>
              Are you sure you want to duplicate the chatbot{" "}
              <b>{listData?.chatbotToChange?.name}</b>?
            </p>
          </DialogBody>
          <DialogActions>
            <Button
              color="secondaryButton"
              onClick={() =>
                setListData((_listData) => ({
                  ..._listData,
                  isModalDuplicateOpen: false,
                }))
              }
              disabled={isDuplicateLoading}
              className="min-w-[100px] h-10"
            >
              Cancel
            </Button>
            <Button
              color="primaryButton"
              onClick={() => handleDuplicateConfirmation()}
              className="min-w-[100px] h-10"
              loading={isDuplicateLoading}
            >
              Duplicate
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
export default ChatbotsPage;
